import {Helmet} from '@dr.pogodin/react-helmet';
import {Dialog, showNotification} from 'platform/components';

import {useState} from 'react';

import {head, isEmpty, isNil, not} from 'ramda';

import {usePostServiceOrderReturnMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  handleApiError,
  logError,
  usePermissions,
} from '@dms/shared';

import {composePath, Nullish, useNavigate} from 'shared/web';

import type {RowData} from 'features/eag-grid';

export function ServiceOrderReturnsCreate() {
  const navigate = useNavigate();

  const [canCreateReturnToWarehouse] = usePermissions({
    permissionKeys: ['returnToWarehouseCreate'],
  });

  const [selectedServiceOrderId, setSelectedServiceOrderId] = useState<string | Nullish>(null);

  const [postServiceOrderReturn, {isLoading: isCreatingServiceOrderReturn}] =
    usePostServiceOrderReturnMutation();

  const handleOnRowSelectionChange = (rows: RowData[]) => {
    setSelectedServiceOrderId(head(rows)?.id);
  };

  const handleDiscard = () => {
    navigate(warehouseRoutes.serviceOrderReturns);
  };

  const handleAssignServiceOrder = () => {
    if (isNil(selectedServiceOrderId) || isEmpty(selectedServiceOrderId)) {
      return logError('No service order is selected');
    }

    postServiceOrderReturn({body: {serviceOrderId: selectedServiceOrderId}})
      .unwrap()
      .then((result) => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));

        navigate(
          composePath(warehouseRoutes.serviceOrderReturnsDetailOverview, {params: {id: result.id}})
        );
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.actions.newServiceOrderReturn')} />
      <Dialog
        isOpen
        title={i18n.t('entity.warehouse.labels.assignServiceOrder')}
        size="large"
        onClose={handleDiscard}
        data-testid={testIds.warehouse.serviceOrderReturnsCreate('assignServiceOrder')}
        buttons={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.labels.discard'),
            onClick: handleDiscard,
            isLoading: isCreatingServiceOrderReturn,
          },
          {
            isLoading: isCreatingServiceOrderReturn,
            isDisabled: not(canCreateReturnToWarehouse) || isNil(selectedServiceOrderId),
            type: 'button',
            title: i18n.t('general.labels.assign'),
            onClick: handleAssignServiceOrder,
            'data-testid': testIds.warehouse.serviceOrderReturnsCreate('assign'),
          },
        ]}
      >
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseServiceOrderPartsReturnSelectOrder}
          onRowSelectionChange={handleOnRowSelectionChange}
          autoHeight
          data-testid={WAREHOUSE_GRID_CODES.warehouseServiceOrderPartsReturnSelectOrder}
        />
      </Dialog>
    </>
  );
}
